import { DeleteIcon } from '@chakra-ui/icons';
import {
    Alert,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertIcon,
    Badge,
    Box,
    Flex,
    IconButton,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    Button,
    useColorModeValue,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../components/Loader';
import { translate } from '../../../i18n';
import {
    tableHeaderTextProps,
    tableProps,
    tableThProps,
    tableThFlexProps,
    tableRowProps,
    tableCellProps,
} from '../../../theme/constants';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useDeleteConversation } from '../hooks/useDeleteConversation';
import {
    ConversationDto,
    useListConversations,
} from '../hooks/useListConversations';

const columnHelper = createColumnHelper<ConversationDto>();

export default function ConversationList() {
    const { selectedOrganization } = useSelectedOrganization();
    const { data, isError, isInitialLoading } = useListConversations(
        selectedOrganization?.id || '',
    );
    // `useListConversations` is `enabled: !!organizationId`, and the id is a
    // zustand value that starts `undefined` and is filled by
    // `GET my-organizations` — so on the first paint of this page, and for the
    // WHOLE SESSION if that read fails or comes back empty (run 100), the
    // conversations query is never sent. React Query v4 reports a disabled
    // query as `isLoading: true`, which is what the raw flag below used to
    // drive: a full-page `<Loader>` that resolves for nobody.
    const readState = classifyRead(isError, isInitialLoading, data);
    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const toast = useToast();
    const navigate = useNavigate();

    const {
        isOpen: isDeleteOpen,
        onOpen: onDeleteOpen,
        onClose: onDeleteClose,
    } = useDisclosure();
    const [deletingConversation, setDeletingConversation] =
        useState<ConversationDto | null>(null);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const { mutateAsync: deleteConversation, isLoading: isDeleting } =
        useDeleteConversation();

    const handleDelete = (conversation: ConversationDto) => {
        setDeletingConversation(conversation);
        onDeleteOpen();
    };

    const handleConfirmDelete = async () => {
        if (!deletingConversation) return;
        try {
            await deleteConversation(deletingConversation.id);
            toast({
                title: translate('whatsapp_conversations.deleted_successfully' as any),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onDeleteClose();
            setDeletingConversation(null);
        } catch (error: any) {
            toast({
                title: translate('whatsapp_conversations.delete_error' as any),
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor('senderPhone', {
                id: 'phone',
                header: () => (
                    <Text {...tableHeaderTextProps}>
                        {translate('whatsapp_conversations.phone' as any)}
                    </Text>
                ),
                cell: (info) => (
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                        {info.getValue()}
                    </Text>
                ),
            }),
            columnHelper.accessor('user', {
                id: 'user',
                header: () => (
                    <Text {...tableHeaderTextProps}>
                        {translate('whatsapp_conversations.user' as any)}
                    </Text>
                ),
                cell: (info) => {
                    const user = info.getValue();
                    return user ? (
                        <Text color={textColor} fontSize="sm">
                            {user.firstName} {user.lastName}
                        </Text>
                    ) : (
                        <Badge colorScheme="gray">
                            {translate('whatsapp_conversations.unknown_user' as any)}
                        </Badge>
                    );
                },
            }),
            columnHelper.accessor('updatedAt', {
                id: 'lastActivity',
                header: () => (
                    <Text {...tableHeaderTextProps}>
                        {translate('whatsapp_conversations.last_activity' as any)}
                    </Text>
                ),
                cell: (info) => (
                    <Text color={textColor} fontSize="sm">
                        {new Date(info.getValue()).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </Text>
                ),
            }),
            columnHelper.accessor('createdAt', {
                id: 'createdAt',
                header: () => (
                    <Text {...tableHeaderTextProps}>
                        {translate('whatsapp_conversations.created_at' as any)}
                    </Text>
                ),
                cell: (info) => (
                    <Text color={textColor} fontSize="sm">
                        {new Date(info.getValue()).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                        })}
                    </Text>
                ),
            }),
            columnHelper.display({
                id: 'actions',
                header: () => (
                    <Text {...tableHeaderTextProps}>
                        {translate('whatsapp_conversations.actions' as any)}
                    </Text>
                ),
                cell: (info) => (
                    <IconButton
                        aria-label="Delete conversation"
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(info.row.original);
                        }}
                    />
                ),
            }),
        ],
        [textColor],
    );

    const table = useReactTable({
        data: data || [],
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (readState === 'loading') {
        return <Loader isLoading />;
    }

    if (readState === 'never-issued') {
        // Not a load and not an empty inbox: nobody asked. The control that
        // fixes it is the organization picker in the navbar, which is why this
        // renders the shared prompt and no retry — retrying an unsent query
        // changes nothing.
        return (
            <PageCard title={translate('whatsapp_conversations.title' as any)}>
                <ScopePrompt gate="organization" />
            </PageCard>
        );
    }

    if (readState === 'unreadable') {
        return (
            <PageCard title={translate('whatsapp_conversations.title' as any)}>
                <Alert status="error">
                    <AlertIcon />
                    {translate('whatsapp_conversations.load_error' as any)}
                </Alert>
            </PageCard>
        );
    }

    return (
        <PageCard title={translate('whatsapp_conversations.title' as any)}>
            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Th
                                        {...tableThProps}
                                        key={header.id}
                                        borderColor={borderColor}
                                        cursor="pointer"
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <Flex {...tableThFlexProps}>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )}
                                            <SortIndicator direction={header.column.getIsSorted()} />
                                        </Flex>
                                    </Th>
                                ))}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => (
                            <Tr
                                key={row.id}
                                {...tableRowProps}
                                onClick={() =>
                                    navigate(
                                        `/admin/whatsapp-conversations/${row.original.id}`,
                                    )
                                }
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <Td
                                        key={cell.id}
                                        {...tableCellProps}
                                        borderColor={borderColor}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </Td>
                                ))}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
                {/* R115-5 — was `(!data || data.length === 0)`. The `!data`
                    half has been dead since run 115 put the three non-answered
                    states behind early returns above, and a reader who trusted
                    that condition would conclude this file handles a refused
                    read here. It does, three arms up. `data &&` says the one
                    true thing: this sentence is a claim about an ANSWERED
                    read that came back empty. */}
                {data && data.length === 0 && (
                    <Flex justify="center" py={8}>
                        <Text color="gray.400">
                            {translate('whatsapp_conversations.no_conversations' as any)}
                        </Text>
                    </Flex>
                )}
            </Box>

            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelButtonRef}
                onClose={onDeleteClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {translate('whatsapp_conversations.delete_conversation' as any)}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {translate('whatsapp_conversations.delete_confirm' as any)}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelButtonRef} onClick={onDeleteClose}>
                                {translate('common.cancel')}
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleConfirmDelete}
                                ml={3}
                                isLoading={isDeleting}
                            >
                                {translate('common.delete')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </PageCard>
    );
}
